/**
 * Generates and prints a user credentials welcome letter
 * Design: Lucent Dental Center branded letter with login info
 */

const ROLE_LABELS_EN = {
  admin: "System Administrator",
  maintenance_manager: "Maintenance Manager",
  it_manager: "IT Manager",
  medical_manager: "Medical Director",
  procurement_manager: "Procurement Manager",
  technician: "Maintenance Technician"
};

export function printCredentialsCard({ fullName, username, password, role, loginUrl }) {
  const roleLabel = ROLE_LABELS_EN[role] || role;
  const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent("https://mnt.lucent.com.sa/login")}&color=6B4C9A`;

  const html = `
<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <title>Welcome - ${fullName}</title>
  <style>
    @page { size: A4; margin: 0; }
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body {
      font-family: 'Segoe UI', 'Helvetica Neue', Arial, sans-serif;
      background: #fff;
      color: #333;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
    .page {
      width: 210mm;
      min-height: 297mm;
      margin: 0 auto;
      padding: 0;
      display: flex;
      flex-direction: column;
    }
    .header {
      text-align: center;
      padding: 40px 40px 24px;
      border-bottom: 3px solid #655dc6;
    }
    .logo {
      width: 160px;
      margin-bottom: 8px;
    }
    .brand-text {
      font-size: 13px;
      color: #655dc6;
      letter-spacing: 3px;
      text-transform: uppercase;
      font-weight: 600;
    }
    .content {
      flex: 1;
      padding: 36px 50px;
    }
    .welcome-badge {
      display: inline-block;
      background: #F3EEFA;
      color: #655dc6;
      padding: 8px 20px;
      border-radius: 20px;
      font-size: 13px;
      font-weight: 600;
      letter-spacing: 1px;
      margin-bottom: 24px;
    }
    .greeting {
      font-size: 26px;
      font-weight: 700;
      color: #655dc6;
      margin-bottom: 6px;
    }
    .greeting-name {
      font-size: 28px;
      font-weight: 700;
      color: #333;
      margin-bottom: 20px;
    }
    .intro-text {
      font-size: 15px;
      color: #555;
      line-height: 1.8;
      margin-bottom: 32px;
    }
    .credentials-box {
      background: #FAFAFA;
      border: 1px solid #E8E0F0;
      border-radius: 12px;
      padding: 28px 32px;
      margin-bottom: 32px;
      display: flex;
      gap: 32px;
      align-items: center;
    }
    .cred-info {
      flex: 1;
    }
    .cred-title {
      font-size: 14px;
      color: #655dc6;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 1px;
      margin-bottom: 16px;
    }
    .cred-row {
      display: flex;
      align-items: center;
      margin-bottom: 12px;
    }
    .cred-label {
      font-size: 13px;
      color: #888;
      width: 100px;
      flex-shrink: 0;
    }
    .cred-value {
      font-size: 16px;
      font-weight: 600;
      color: #333;
      font-family: 'Courier New', monospace;
      background: #fff;
      padding: 6px 14px;
      border-radius: 6px;
      border: 1px solid #E0D8EB;
    }
    .cred-role {
      font-size: 13px;
      color: #655dc6;
      margin-top: 4px;
      font-weight: 500;
    }
    .qr-section {
      text-align: center;
      flex-shrink: 0;
    }
    .qr-section img {
      width: 120px;
      height: 120px;
      border-radius: 8px;
    }
    .qr-label {
      font-size: 10px;
      color: #999;
      margin-top: 6px;
    }
    .login-url-box {
      background: #F8F5FC;
      border: 1px dashed #C4B5D9;
      border-radius: 8px;
      padding: 14px 20px;
      text-align: center;
      margin-bottom: 32px;
    }
    .login-url-label {
      font-size: 11px;
      color: #999;
      margin-bottom: 4px;
      text-transform: uppercase;
      letter-spacing: 1px;
    }
    .login-url {
      font-size: 14px;
      color: #655dc6;
      font-weight: 600;
      word-break: break-all;
    }
    .note {
      font-size: 13px;
      color: #888;
      line-height: 1.7;
      padding: 16px 20px;
      background: #FFFBEB;
      border-radius: 8px;
      border-left: 3px solid #F59E0B;
    }
    .footer {
      background: #655dc6;
      color: white;
      padding: 20px 50px;
      text-align: center;
    }
    .footer-title {
      font-size: 12px;
      font-weight: 600;
      letter-spacing: 2px;
      text-transform: uppercase;
      margin-bottom: 8px;
      opacity: 0.85;
    }
    .footer-line {
      font-size: 13px;
      margin-bottom: 4px;
      opacity: 0.9;
    }
    .footer-brand {
      font-size: 12px;
      opacity: 0.7;
      margin-top: 4px;
    }
    @media print {
      body { background: white; }
      .page { margin: 0; }
    }
  </style>
</head>
<body>
  <div class="page">
    <div class="header">
      <img src="/lucent-logo.png" alt="Lucent" class="logo" />
      <div class="brand-text">Maintenance Management System</div>
    </div>

    <div class="content">
      <div class="welcome-badge">WELCOME TO THE TEAM</div>
      
      <div class="greeting">Welcome,</div>
      <div class="greeting-name">${fullName}</div>
      
      <p class="intro-text">
        We are pleased to welcome you as part of the Lucent Dental Center maintenance team.
        Below you will find your login credentials for the Maintenance Management System.
        Please keep this information secure and do not share it with others.
      </p>

      <div class="credentials-box">
        <div class="cred-info">
          <div class="cred-title">Your Login Credentials</div>
          <div class="cred-row">
            <span class="cred-label">Username</span>
            <span class="cred-value">${username}</span>
          </div>
          <div class="cred-row">
            <span class="cred-label">Password</span>
            <span class="cred-value">${password}</span>
          </div>
          <div class="cred-role">Role: ${roleLabel}</div>
        </div>
        <div class="qr-section">
          <img src="${qrUrl}" alt="Login QR" />
          <div class="qr-label">Scan to login</div>
        </div>
      </div>

      <div class="login-url-box">
        <div class="login-url-label">System Login URL</div>
        <div class="login-url">${loginUrl}</div>
      </div>

      <div class="note">
        <strong>Important:</strong> Please change your password after your first login for security purposes.
        If you experience any issues, contact the Maintenance Department.
      </div>
    </div>

    <div class="footer">
      <div class="footer-title">Contact & Inquiries</div>
      <div class="footer-line">Maintenance Department - Tel: +966 11 22 000 11 ext.204</div>
      <div class="footer-brand">Lucent Dental Clinic | lucent.com.sa | +966 11 22 000 11</div>
    </div>
  </div>

  <script>
    window.onload = function() {
      setTimeout(function() { window.print(); }, 500);
    };
  </script>
</body>
</html>`;

  const printWindow = window.open("", "_blank");
  if (printWindow) {
    printWindow.document.write(html);
    printWindow.document.close();
  }
}
